// Telegram CloudStorage with safe chunking (values are capped at 4 KB each).
// Adapted from the raw window.Telegram.WebApp.CloudStorage API.

function wa(): TelegramWebApp {
  const w = window.Telegram?.WebApp;
  if (!w) throw new Error('Telegram WebApp not available');
  return w;
}

type CS = TelegramWebApp['CloudStorage'];

const CHUNK = 3500; // safety margin under the 4 KB / value limit
const MAX_CHUNKS = 800;
const META_PREFIX = '{"chunks":';

const chunkKey = (key: string, i: number) => `${key}__${i}`;

function getRaw(cs: CS, key: string): Promise<string | null> {
  return new Promise((res, rej) => cs.getItem(key, (e, v) => (e ? rej(e) : res(v ?? null))));
}
function setRaw(cs: CS, key: string, value: string): Promise<void> {
  return new Promise((res, rej) => cs.setItem(key, value, (e) => (e ? rej(e) : res())));
}
function removeRaw(cs: CS, key: string): Promise<void> {
  return new Promise((res, rej) => cs.removeItem(key, (e) => (e ? rej(e) : res())));
}
function removeManyRaw(cs: CS, keys: string[]): Promise<void> {
  if (keys.length === 0) return Promise.resolve();
  return new Promise((res, rej) => cs.removeItems(keys, (e) => (e ? rej(e) : res())));
}
function getManyRaw(cs: CS, keys: string[]): Promise<Record<string, string>> {
  if (keys.length === 0) return Promise.resolve({});
  return new Promise((res, rej) =>
    cs.getItems(keys, (e, v) => (e ? rej(e) : res((v ?? {}) as Record<string, string>))),
  );
}

function parseChunkCount(head: string | null): number {
  if (!head || !head.startsWith(META_PREFIX)) return 0;
  try {
    const parsed = JSON.parse(head) as { chunks?: number };
    if (typeof parsed.chunks === 'number' && parsed.chunks > 0 && parsed.chunks <= MAX_CHUNKS) {
      return parsed.chunks;
    }
  } catch {
    /* not a meta header */
  }
  return 0;
}

/** Write a value; chunked + commit-pointer so a mid-write crash never yields a
 *  half-written value (reads return the previous value or null). */
export async function csSet(key: string, value: string): Promise<void> {
  const cs = wa().CloudStorage;
  const oldChunks = parseChunkCount(await getRaw(cs, key));

  if (value.length <= 4096 && !value.startsWith(META_PREFIX)) {
    await setRaw(cs, key, value);
    if (oldChunks > 0) {
      const tail = Array.from({ length: oldChunks }, (_, i) => chunkKey(key, i));
      await removeManyRaw(cs, tail).catch(() => {});
    }
    return;
  }

  const parts: string[] = [];
  for (let i = 0; i < value.length; i += CHUNK) parts.push(value.slice(i, i + CHUNK));
  if (parts.length > MAX_CHUNKS) throw new Error('value too large for CloudStorage');

  for (let i = 0; i < parts.length; i++) await setRaw(cs, chunkKey(key, i), parts[i]!);
  await setRaw(cs, key, JSON.stringify({ chunks: parts.length }));
  if (oldChunks > parts.length) {
    const tail: string[] = [];
    for (let i = parts.length; i < oldChunks; i++) tail.push(chunkKey(key, i));
    await removeManyRaw(cs, tail).catch(() => {});
  }
}

export async function csGet(key: string): Promise<string | null> {
  const cs = wa().CloudStorage;
  const head = await getRaw(cs, key);
  if (head === null || head === '') return head;
  const chunks = parseChunkCount(head);
  if (chunks > 0) {
    const keys = Array.from({ length: chunks }, (_, i) => chunkKey(key, i));
    const got = await getManyRaw(cs, keys);
    return keys.map((k) => got[k] ?? '').join('');
  }
  return head;
}

export async function csRemove(key: string): Promise<void> {
  const cs = wa().CloudStorage;
  const chunks = parseChunkCount(await getRaw(cs, key));
  await removeRaw(cs, key);
  if (chunks > 0) {
    const tail = Array.from({ length: chunks }, (_, i) => chunkKey(key, i));
    await removeManyRaw(cs, tail).catch(() => {});
  }
}

export async function csGetJson<T>(key: string, fallback: T): Promise<T> {
  const raw = await csGet(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export async function csSetJson<T>(key: string, value: T): Promise<void> {
  await csSet(key, JSON.stringify(value));
}
